import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import './clientWalletCharges.css';

const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ClientWalletCharges({ bookingId }) {
    // Payments restructure Phase 2: the endpoint now returns { transactions, receipts }.
    // A card payment is a RECEIPT (shown once, blue "Card payment"); the card wallet
    // round-trip rows are already hidden server-side, so we just render what we receive.
    const [transactions, setTransactions] = useState([]);
    const [receipts, setReceipts] = useState([]);
    const [pending, setPending] = useState(0);
    // The rest of the pending payload. Without it this card showed the client
    // "Pending AED x" on a booking whose invoice they had already paid — by
    // card, by payment link, by transfer, or on a consolidated monthly invoice
    // (2026-08-30). It also had nothing to show them when the money came in on
    // a rail that writes no wallet row and no receipt.
    const [paidInfo, setPaidInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [receiptLoading, setReceiptLoading] = useState(null);

    useEffect(() => {
        if (!bookingId) return;
        let cancelled = false;
        (async () => {
            try {
                const [res, pcRes] = await Promise.all([
                    axios.get(`/api/client-requests/wallet/bookings/${bookingId}/transactions`),
                    // Still-pending amount on this booking (total − settled), billed on
                    // the next invoice — shown here too (2026-07-21).
                    axios.get(`/api/client-requests/bookings/${bookingId}/price-changes`).catch(() => null),
                ]);
                const data = res.data || {};
                if (!cancelled) {
                    // Tolerate the legacy array shape as well as { transactions, receipts }.
                    setTransactions(Array.isArray(data) ? data : (data.transactions || []));
                    setReceipts(Array.isArray(data) ? [] : (data.receipts || []));
                    setPending(Number(pcRes?.data?.pending || 0));
                    setPaidInfo(pcRes?.data || null);
                }
            } catch {
                if (!cancelled) { setTransactions([]); setReceipts([]); }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [bookingId]);

    const linkPayments = paidInfo?.link_payments || [];
    const paidOnInvoice = !!paidInfo?.paid_on_invoice;

    // Keep the card when a payment link or the invoice itself settled this
    // booking: that IS the client's payment, and hiding it left them looking at
    // a booking with no record of the money they sent.
    if (loading || (transactions.length === 0 && receipts.length === 0
        && linkPayments.length === 0 && !paidOnInvoice && pending <= 0.009)) return null;

    const formatDate = (d) => {
        if (!d) return '—';
        return DateTime.fromISO(d, { zone: 'Asia/Dubai' }).toFormat('dd MMM yy, HH:mm');
    };

    // Open the Zoho receipt PDF for a card payment in a NEW TAB (2026-07-16:
    // view, don't auto-download). The tab is opened synchronously on the click so
    // the popup blocker doesn't kill it after the await, then pointed at the blob.
    const downloadReceipt = async (r) => {
        if (!r.zoho_payment_id || receiptLoading === r.id) return;
        setReceiptLoading(r.id);
        const win = window.open('', '_blank');
        try {
            const res = await axios.get(
                `/api/client-requests/invoices/payments/${r.zoho_payment_id}/pdf`,
                { responseType: 'blob' }
            );
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            if (win) win.location.href = url;
            else window.open(url, '_blank');
        } catch {
            if (win) win.close();
            alert('Failed to open receipt.');
        } finally {
            setReceiptLoading(null);
        }
    };

    const totalCharged = transactions
        .filter(t => t.type === 'booking_charge')
        .reduce((s, t) => s + Math.abs(parseFloat(t.amount)), 0)
        + receipts.reduce((s, r) => s + Math.abs(parseFloat(r.amount)), 0);
    const totalRefunded = transactions
        .filter(t => t.type === 'booking_refund')
        .reduce((s, t) => s + Math.abs(parseFloat(t.amount)), 0)
        + receipts.reduce((s, r) => s + Math.abs(parseFloat(r.refunded_amount || 0)), 0);
    const netAmount = totalCharged - totalRefunded;

    // The base wallet payment names its source. Card payments are receipts now, so a
    // booking_charge that survives here is always wallet-funded.
    const getReason = (t) => {
        const desc = t.description || '';
        if (/^(Booking )?#?\d+ charge$/i.test(desc.trim())) return t.type === 'booking_charge' ? 'Wallet Payment' : 'Refund';
        if (desc.includes('Refund for deleted')) return 'Booking Cancelled';
        if (t.type === 'booking_refund') return 'Refund';
        if (desc.includes('Type:') || desc.includes('Added:') || desc.includes('Removed:') ||
            desc.includes('Amenities') || desc.includes('Time changed') || desc.includes('Cleaners:')) return 'Booking Change';
        if (desc.toLowerCase().includes('price increase')) return 'Price Increase';
        if (desc.toLowerCase().includes('price decrease')) return 'Price Decrease';
        return t.type === 'booking_charge' ? 'Wallet Payment' : 'Refund';
    };

    const getChangeDetails = (t) => {
        const desc = t.description || '';
        const newMatch = desc.match(/#\d+: (.+?)(?:\s*\([\d.]+\s*→\s*[\d.]+\))?$/);
        if (newMatch) {
            return newMatch[1].replace(/\s*\([\d.]+\s*→\s*[\d.]+\)/, '').split(', ').filter(Boolean);
        }
        if (desc.toLowerCase().includes('price increase')) return ['Price went up'];
        if (desc.toLowerCase().includes('price decrease')) return ['Price went down'];
        return [];
    };

    // Merge card receipts + wallet transactions into one chronological list.
    const entries = [
        ...receipts.map((r) => ({
            key: `r-${r.id}`,
            row: 'receipt',
            date: r.gateway_captured_at || r.created_at,
            receipt: r,
        })),
        ...transactions.map((t) => ({
            key: `t-${t.id}`,
            row: 'txn',
            date: t.created_at,
            txn: t,
        })),
    ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return (
        <div className="client-section">
            <h3>Transactions</h3>
            <div className="cwc">
            {paidOnInvoice && (
                <div className="cwc__paidNote">
                    Paid on invoice <strong>{paidInfo?.invoice_number || ''}</strong>. Nothing is outstanding on this booking.
                </div>
            )}

            <div className="cwc__summary">
                <div className="cwc__summaryItem">
                    <span className="cwc__summaryLabel">Charged</span>
                    <span className="cwc__summaryValue cwc__summaryValue--charge">{money(totalCharged)}</span>
                </div>
                {totalRefunded > 0 && (
                    <div className="cwc__summaryItem">
                        <span className="cwc__summaryLabel">Refunded</span>
                        <span className="cwc__summaryValue cwc__summaryValue--refund">{money(totalRefunded)}</span>
                    </div>
                )}
                <div className="cwc__summaryItem cwc__summaryItem--net">
                    <span className="cwc__summaryLabel">Net</span>
                    <span className="cwc__summaryValue">{money(netAmount)}</span>
                </div>
                {pending > 0.009 && (
                    <div className="cwc__summaryItem">
                        <span className="cwc__summaryLabel">Pending</span>
                        <span className="cwc__summaryValue cwc__summaryValue--pending" title="Billed on your next invoice">
                            {money(pending)}
                        </span>
                    </div>
                )}
            </div>

            <div className="cwc__list">
                {linkPayments.map((p) => (
                    <div key={`link-${p.id}`} className="cwc__item">
                        <div className="cwc__itemMain">
                            <div className="cwc__itemTop">
                                <span className="cwc__badge cwc__badge--card">Paid by payment link</span>
                                <span className="cwc__itemAmount">{money(p.amount)}</span>
                            </div>
                            <div className="cwc__itemMeta">{formatDate(p.paid_at)}</div>
                        </div>
                    </div>
                ))}
                {entries.map((e) => {
                    if (e.row === 'receipt') {
                        const r = e.receipt;
                        const amt = Math.abs(parseFloat(r.amount));
                        return (
                            <div key={e.key} className="cwc__item">
                                <div className="cwc__itemLeft">
                                    <div className="cwc__itemTop">
                                        {/* P1 — names the row for what it is: a
                                            payment FOR THIS BOOKING that was made
                                            by card. Never "top-up", never
                                            "deposit". The wallet is not involved. */}
                                        <span className="cwc__badge cwc__badge--card">Booking payment — card</span>
                                        <span className="cwc__itemDate">{formatDate(e.date)}</span>
                                    </div>
                                    {r.zoho_payment_id && (
                                        <div className="cwc__details">
                                            <span
                                                className="cwc__receiptLink"
                                                role="button"
                                                tabIndex={0}
                                                style={{ cursor: receiptLoading === r.id ? 'wait' : 'pointer' }}
                                                onClick={() => downloadReceipt(r)}
                                                onKeyDown={(ev) => { if (ev.key === 'Enter') downloadReceipt(r); }}
                                            >
                                                {receiptLoading === r.id ? 'Loading…' : 'Receipt'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="cwc__itemRight">
                                    <span className="cwc__itemAmount cwc__itemAmount--card">
                                        -{money(amt)}
                                    </span>
                                </div>
                            </div>
                        );
                    }

                    const t = e.txn;
                    const isRefund = t.type === 'booking_refund';
                    const amt = Math.abs(parseFloat(t.amount));
                    const details = getChangeDetails(t);
                    return (
                        <div key={e.key} className={`cwc__item ${isRefund ? 'cwc__item--refund' : ''}`}>
                            <div className="cwc__itemLeft">
                                <div className="cwc__itemTop">
                                    <span className={`cwc__badge ${isRefund ? 'cwc__badge--refund' : 'cwc__badge--charge'}`}>
                                        {getReason(t)}
                                    </span>
                                    <span className="cwc__itemDate">{formatDate(t.created_at)}</span>
                                </div>
                                {details.length > 0 && (
                                    <div className="cwc__details">
                                        {details.map((d, i) => <span key={i} className="cwc__detailChip">{d}</span>)}
                                    </div>
                                )}
                            </div>
                            <div className="cwc__itemRight">
                                <span className={`cwc__itemAmount ${isRefund ? 'cwc__itemAmount--refund' : 'cwc__itemAmount--charge'}`}>
                                    {isRefund ? '+' : '-'}{money(amt)}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
            </div>
        </div>
    );
}
